import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../assets/images/logo.png";

const Navbar = () => {
  const isAuthenticated = !!localStorage.getItem("credentials");
  const role = localStorage.getItem("role");
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrolled]);

  const handleLogout = () => {
    localStorage.removeItem("credentials");
    localStorage.removeItem("username");
    localStorage.removeItem("role");
    window.location.href = "/login";
  };

  // Base navigation links
  const baseNavLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/products", label: "Products" },
    { path: "/blog", label: "Blog" },
    { path: "/career", label: "Career" },
    { path: "/contact", label: "Contact" },
  ];

  // Add "Our Services" only if user is authenticated and has admin role
  const navLinks =
    isAuthenticated && role === "ROLE_USER"
      ? [
          ...baseNavLinks.slice(0, 4), // Home, About, Products, Blog
          { path: "/services", label: "Our Services" },
          ...baseNavLinks.slice(4), // Contact
        ]
      : baseNavLinks;

  return (
    <motion.nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-gray-800"
      style={{
        background:
          "linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)",
      }}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          {/* Logo and Tagline */}
          <Link
            to="/"
            className="flex flex-col items-start justify-center text-decoration-none"
            style={{ minWidth: 0 }}
          >
            <div className="flex items-center">
              <img
                src={logo}
                alt="Pranayuv Logo"
                style={{
                  height: "70px",
                  width: "70px",
                  objectFit: "contain",
                  marginRight: "10px",
                }}
              />
              <div className="flex flex-col justify-center">
                <span
                  style={{
                    fontSize: "1.7rem",
                    fontWeight: "bold",
                    color: "#fff",
                    lineHeight: 1,
                  }}
                >
                  Pranayuv
                </span>
                <span
                  style={{
                    fontSize: "0.85rem",
                    color: "#fff",
                    marginTop: "2px",
                    lineHeight: 1,
                  }}
                >
                  Empowering Lives Through Innovation
                </span>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex flex-1 justify-center gap-x-0.4 items-center">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`no-underline px-4 py-2 rounded-md text-[15px] font-semibold uppercase tracking-wide bg-transparent text-white transition-colors duration-200 hover:bg-[#1a4a5a] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#81d4fa] ${
                  location.pathname === link.path
                    ? "bg-[#1a4a5a] text-white"
                    : ""
                }`}
                style={{
                  minWidth: "100px",
                  textAlign: "center",
                  whiteSpace:
                    link.label === "Our Services" ? "nowrap" : undefined,
                }}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Auth Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                <Link
                  to={
                    role === "ROLE_ADMIN"
                      ? "/admin-dashboard"
                      : "/user-dashboard"
                  }
                  className={`px-4 py-2 mx-1 rounded-md text-[15px] font-semibold uppercase tracking-wide bg-transparent text-white border-none text-decoration-none transition-colors duration-200 hover:bg-[#1a4a5a] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#81d4fa] ${
                    location.pathname ===
                    (role === "ROLE_ADMIN"
                      ? "/admin-dashboard"
                      : "/user-dashboard")
                      ? "bg-[#1a4a5a] text-white"
                      : ""
                  }`}
                  style={{ minWidth: "100px", textAlign: "center" }}
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 mx-1 rounded-md text-[15px] font-semibold uppercase tracking-wide bg-transparent text-white border-none text-decoration-none transition-colors duration-200 hover:bg-[#1a4a5a] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#81d4fa]"
                  style={{ minWidth: "100px", textAlign: "center" }}
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/BMI-calculator"
                  className="px-4 py-2 mx-1 rounded-md text-[15px] font-semibold uppercase tracking-wide bg-transparent text-white border-none text-decoration-none transition-colors duration-200 hover:bg-[#1a4a5a] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#81d4fa]"
                  style={{
                    minWidth: "100px",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  BMI Calculator
                </Link>
                <Link
                  to="/login"
                  className="px-4 py-2 mx-1 rounded-md text-[15px] font-semibold uppercase tracking-wide bg-transparent text-white border-none text-decoration-none transition-colors duration-200 hover:bg-[#1a4a5a] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#81d4fa]"
                  style={{ minWidth: "100px", textAlign: "center" }}
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="px-4 py-2 mx-1 rounded-md text-[15px] font-semibold uppercase tracking-wide bg-transparent text-white border-none text-decoration-none transition-colors duration-200 hover:bg-[#1a4a5a] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#81d4fa]"
                  style={{
                    minWidth: "100px",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            className="lg:hidden text-white p-2 focus:outline-none focus:ring-2 focus:ring-[#81d4fa]"
            onClick={() => setMenuOpen(!menuOpen)}
            whileTap={{ scale: 0.95 }}
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="lg:hidden bg-[#1a1a1a]"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="container mx-auto px-4 py-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block w-full text-left py-3 px-4 text-[15px] font-semibold uppercase tracking-wide text-white hover:text-[#1a4a5a] transition-colors duration-200 ${
                    location.pathname === link.path ? "text-[#0288d1]" : ""
                  } text-decoration-none focus:outline-none focus:ring-2 focus:ring-[#81d4fa]`}
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="border-t border-gray-800 my-4" />
              {isAuthenticated ? (
                <>
                  <Link
                    to={
                      role === "ROLE_ADMIN"
                        ? "/admin-dashboard"
                        : "/user-dashboard"
                    }
                    className="block w-full text-left py-3 px-4 text-[15px] font-semibold uppercase tracking-wide text-white hover:text-[#1a4a5a] transition-colors duration-200 text-decoration-none focus:outline-none focus:ring-2 focus:ring-[#81d4fa]"
                    onClick={() => setMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMenuOpen(false);
                    }}
                    className="block w-full text-left py-3 px-4 border border-[#81d4fa] text-white rounded hover:bg-[#0288d1] hover:text-white transition-colors duration-200 font-semibold uppercase text-[15px] text-decoration-none focus:outline-none focus:ring-2 focus:ring-[#81d4fa]"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="block w-full text-left py-3 px-4 border border-[#81d4fa] text-white rounded hover:bg-[#0288d1] hover:text-white transition-colors duration-200 font-semibold uppercase text-[15px] text-decoration-none focus:outline-none focus:ring-2 focus:ring-[#81d4fa]"
                    onClick={() => setMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className="block w-full text-left py-3 px-4 border border-[#81d4fa] text-white rounded hover:bg-[#0288d1] hover:text-white transition-colors duration-200 font-semibold uppercase text-[15px] text-decoration-none focus:outline-none focus:ring-2 focus:ring-[#81d4fa]"
                    onClick={() => setMenuOpen(false)}
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .navbar button:hover, .navbar a:hover {
          background: linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%) !important;
          color: #fff !important;
          border: none !important;
          outline: none !important;
        }
      `}</style>
    </motion.nav>
  );
};

export default Navbar;
